import { runSyncCycle } from './syncEngine';

/**
 * Cadence triggers for the sync engine — "LiftX Sync Blueprint" §9. Three
 * moments trigger a cycle: the scheduler starting (i.e. login, since the
 * caller only starts this while authenticated), a 5-minute interval while
 * the tab is visible, and connectivity/foreground returning — the exact
 * idiom `AuthProvider.tsx`'s own reconciliation effect already uses for the
 * same reason: no polling while backgrounded or offline, because there is
 * nothing to gain from it and a real cost (battery, a doomed fetch) to lose.
 *
 * Module-level, not a React effect itself, so `AuthProvider` (the one place
 * that knows "authenticated" vs "not") can start/stop it with a plain
 * function call from its own effect rather than this file needing to know
 * anything about auth state.
 */

const INTERVAL_MS = 5 * 60 * 1000;

let intervalId: ReturnType<typeof setInterval> | null = null;

function tick(): void {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  void runSyncCycle();
}

function onOnline(): void {
  void runSyncCycle();
}

function onVisibility(): void {
  if (document.visibilityState === 'visible') void runSyncCycle();
}

/** Idempotent — calling this while already running is a no-op, not a second timer. */
export function startSyncScheduler(): void {
  if (intervalId !== null) return;

  void runSyncCycle();
  intervalId = setInterval(tick, INTERVAL_MS);
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisibility);
}

export function stopSyncScheduler(): void {
  if (intervalId !== null) clearInterval(intervalId);
  intervalId = null;
  window.removeEventListener('online', onOnline);
  document.removeEventListener('visibilitychange', onVisibility);
}
